import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { useProgramGeneratorProgressStore } from 'src/store/programGeneratorProgressStore';
import { clearGeneratedProgram } from 'src/store/programStore';
import { clearSubscriptionData } from 'src/store/subscriptionStore';

export interface Certificate {
  name: string;
  fileUrl?: string;
  fileName?: string;
}

export interface SessionPricing {
  duration: number; // minutes
  price: number;
}

export interface SocialLink {
  platform: string;
  url: string;
}

export type User = {
  uid?: string;
  name: string;
  surname: string;
  email: string;
  password?: string;

  // Profile Fields
  avatarUrl?: string;
  bio?: string;
  experience?: number; // years
  experienceLevel?: string;
  timezone?: string;
  weekly_schedule?: any;
  onboarding?: {
    lastCompletedStep?: number;
    onboardingSkipped?: boolean;
    step1_experience?: string | string[];
    step2_client_types?: string[];
    step3_offers?: string[];
    step4_schedule?: {
      timezone: string;
      weekly_schedule?: any;
    };
  };
  certificates?: Certificate[];
  expertise?: string;
  trainingStyle?: string;
  pricing?: SessionPricing[];
  location?: string;
  facility?: string;
  createdAt?: string | number;
  review?: string | number;
  introductionVideoUrl?: string;
  introductionVideoName?: string;
  introductionVideoSize?: number;
  introductionVideoDuration?: number;
  is_online?: boolean;
  last_seen?: string | number | null;
  socialLinks?: SocialLink[];
};

type AuthState = {
  token: string | null;
  currentUser: User | null;
  setToken: (token: string, user?: User) => void;
  updateProfile: (updates: Partial<User>) => void;
  logout: () => void;
  isLoggedIn: boolean;
};

import { auth } from 'src/config/firebase';

export const sessionStore = create<AuthState>()(
  persist(
    (set, get) => ({
      isLoggedIn: false,
      token: null,
      currentUser: null,
      setToken: (token: string, user?: User) =>
        set({
          token,
          currentUser: user
            ? {
                ...user,
                uid: user.uid || auth.currentUser?.uid,
              }
            : null,
          isLoggedIn: true,
        }),
      updateProfile: (updates: Partial<User>) =>
        set((state) => {
          if (!state.currentUser) return state;
          const updatedUser = {
            ...state.currentUser,
            ...updates,
            uid: updates.uid || state.currentUser.uid || auth.currentUser?.uid,
          };
          return { currentUser: updatedUser };
        }),
      logout: () => {
        const currentUid = get().currentUser?.uid;
        if (currentUid) {
          import('src/services/presence/trainerPresence').then(({ setTrainerOnlineStatus }) => {
            setTrainerOnlineStatus(currentUid, false);
          });
          import('src/services/push/registerPush').then(({ unsubscribeBrowserPush }) => {
            unsubscribeBrowserPush(currentUid, 'trainer');
          });
        }
        useProgramGeneratorProgressStore.getState().resetProgress();
        clearGeneratedProgram();
        clearSubscriptionData();
        set({ token: null, currentUser: null, isLoggedIn: false });
      },
    }),
    {
      name: 'auth-storage', // key in localStorage
    },
  ),
);
